/**
 * Coordinate Transformer Utilities
 *
 * Converts between SCS (Simulator Coordinate System) and real-world GPS coordinates.
 *
 * Coordinate Systems:
 * - SCS: Radar-centered local coordinates
 *   - X_scs: downrange (forward along hole)
 *   - Y_scs: vertical (up/down)
 *   - Z_scs: lateral (left/right side)
 *
 * - ENU: East-North-Up local tangent plane
 *   - dE: East offset in meters
 *   - dN: North offset in meters
 *   - dU: Up offset in meters
 *
 * - World: GPS geodetic coordinates
 *   - lat: Latitude in decimal degrees
 *   - lon: Longitude in decimal degrees
 *   - alt: Altitude in meters above sea level
 *
 * Conversion Flow: SCS → ENU → World GPS
 *
 * Direction and origin are derived from WorldCoordinates in SystemConfig:
 * - Origin: GPS position that serves as the (0,0,0) reference point
 * - Reference: GPS position used with Origin to determine course direction
 */

/** GPS origin point with required coordinates */
type GpsOrigin = {
  Latitude: number;
  Longitude: number;
  Altitude: number;
};

/** GPS reference point for direction (altitude not needed) */
type GpsReference = {
  Latitude: number;
  Longitude: number;
};

/** World coordinates configuration for coordinate transformation */
export interface WorldCoordinatesConfig {
  Origin: GpsOrigin;
  Reference: GpsReference;
}

/** 3D point in SCS coordinates [X_scs, Y_scs, Z_scs] */
export type ScsPoint = [number, number, number];

/** 3D point in ENU coordinates [dE, dN, dU] */
type EnuPoint = [number, number, number];

/** GPS coordinate point */
type GpsPoint = {
  lat: number;
  lon: number;
  alt: number;
};

/** 2D unit vector [east, north] */
type UnitVector2D = [number, number];

/** Course orientation axes derived from UTM anchors */
type CourseAxes = {
  /** Unit vector along course direction (Origin → Reference) */
  xAxis: UnitVector2D;
  /** Unit vector perpendicular to course (lateral direction) */
  yAxis: UnitVector2D;
};

// Earth radius in meters (WGS84)
const EARTH_RADIUS = 6378137.0;

/**
 * Normalize a 2D vector to unit length
 *
 * @param vx - X component
 * @param vy - Y component
 * @returns Normalized vector [x, y]
 * @throws Error if vector has zero length
 */
function normalize(vx: number, vy: number): UnitVector2D {
  const length = Math.sqrt(vx * vx + vy * vy);
  if (length === 0) {
    throw new Error("Cannot normalize zero-length vector");
  }
  return [vx / length, vy / length];
}

/**
 * Build course orientation axes from WorldCoordinates GPS points
 *
 * The WorldCoordinates contains two GPS coordinate pairs:
 * - Origin: Starting point (lat/lon)
 * - Reference: End point (lat/lon)
 *
 * The vector from Origin → Reference defines the course direction (X axis).
 * The Y axis is perpendicular (90° counter-clockwise).
 *
 * Uses small-offset approximation to convert GPS delta to ENU offsets,
 * which gives us the direction vector in the local tangent plane.
 *
 * @param worldCoords - World coordinates configuration from system config
 * @returns Course axes (x: course direction, y: lateral direction)
 * @throws Error if Origin and Reference are too close (invalid course orientation)
 */
function buildCourseAxes(worldCoords: WorldCoordinatesConfig): CourseAxes {
  const { Origin, Reference } = worldCoords;

  // Convert GPS delta to ENU offsets (East, North)
  const lat0Rad = (Origin.Latitude * Math.PI) / 180.0;
  const dLatRad = ((Reference.Latitude - Origin.Latitude) * Math.PI) / 180.0;
  const dLonRad = ((Reference.Longitude - Origin.Longitude) * Math.PI) / 180.0;

  // Calculate East and North offsets in meters
  const dE = dLonRad * EARTH_RADIUS * Math.cos(lat0Rad);
  const dN = dLatRad * EARTH_RADIUS;

  // Check if vector is too small (Origin and Reference are too close)
  const length = Math.sqrt(dE * dE + dN * dN);
  const MIN_VECTOR_LENGTH = 0.001; // 1mm minimum distance

  if (length < MIN_VECTOR_LENGTH) {
    throw new Error(
      `Invalid world coordinates: Origin and Reference GPS coordinates are too close (distance: ${length.toFixed(6)}m). ` +
        `Origin: [${Origin.Latitude}, ${Origin.Longitude}], ` +
        `Reference: [${Reference.Latitude}, ${Reference.Longitude}]. ` +
        `Cannot determine course orientation.`
    );
  }

  // X axis = course direction (normalized)
  const xAxis = normalize(dE, dN);

  // Y axis = perpendicular to X, 90° counter-clockwise
  const yAxis: UnitVector2D = [-xAxis[1], xAxis[0]];

  return { xAxis, yAxis };
}

/**
 * Convert SCS point to ENU (East-North-Up) coordinates
 *
 * Projects SCS coordinates onto the local tangent plane using course axes.
 *
 * Mapping:
 * - Ground plane: (X_scs, Z_scs) → (East, North)
 * - Vertical: Y_scs → Up
 *
 * Formula:
 * - dE = X_scs * x_axis.e + (-Z_scs) * y_axis.e
 * - dN = X_scs * x_axis.n + (-Z_scs) * y_axis.n
 * - dU = Y_scs
 *
 * Note: Z_scs is negated to correct the left/right direction mapping.
 *
 * @param pointScs - Point in SCS coordinates [X_scs, Y_scs, Z_scs]
 * @param xAxis - Course direction unit vector [east, north]
 * @param yAxis - Lateral direction unit vector [east, north]
 * @returns ENU offsets in meters [dE, dN, dU]
 */
function scsToEnu(
  pointScs: ScsPoint,
  xAxis: UnitVector2D,
  yAxis: UnitVector2D
): EnuPoint {
  const [xScs, yScs, zScs] = pointScs;
  const [xE, xN] = xAxis;
  const [yE, yN] = yAxis;

  // Project forward (X_scs) + lateral (Z_scs) into East/North
  // Note: Negating Z_scs to correct left/right direction
  const dE = xScs * xE + -zScs * yE;
  const dN = xScs * xN + -zScs * yN;

  // Vertical = SCS Y
  const dU = yScs;

  return [dE, dN, dU];
}

/**
 * Convert ENU offsets to geodetic coordinates (lat/lon/alt)
 *
 * Uses small-offset approximation suitable for golf-scale distances (< 1km).
 * The origin GPS position serves as the reference point (0, 0, 0).
 *
 * Formula:
 * - dLat = (dN / R) * (180 / π)
 * - dLon = (dE / (R * cos(lat0))) * (180 / π)
 * - alt = alt0 + dU
 *
 * Where R is Earth's radius and lat0 is the origin's latitude.
 *
 * @param origin - GPS origin position (reference point)
 * @param dE - East offset in meters
 * @param dN - North offset in meters
 * @param dU - Up offset in meters
 * @returns GPS coordinates {lat, lon, alt}
 */
function enuToGeodetic(
  origin: GpsOrigin,
  dE: number,
  dN: number,
  dU: number
): GpsPoint {
  const lat0Rad = (origin.Latitude * Math.PI) / 180.0;

  const dLat = (dN / EARTH_RADIUS) * (180.0 / Math.PI);
  const dLon = (dE / (EARTH_RADIUS * Math.cos(lat0Rad))) * (180.0 / Math.PI);

  return {
    lat: origin.Latitude + dLat,
    lon: origin.Longitude + dLon,
    alt: origin.Altitude + dU,
  };
}

/**
 * Convert SCS coordinates to world GPS coordinates
 *
 * Main conversion function that combines all transformation steps:
 * SCS → ENU → World GPS
 *
 * This is the primary function to use for converting radar coordinates
 * to map-displayable GPS coordinates.
 *
 * @param pointScs - Point in SCS coordinates [X_scs, Y_scs, Z_scs]
 * @param worldCoords - World coordinates config (Origin for GPS origin, Reference for direction)
 * @returns GPS coordinates {lat, lon, alt}
 *
 * @example
 * ```typescript
 * const pinScs: ScsPoint = [425, 0, 75]; // Pin at 425m forward, 75m right
 * const pinGps = scsToWorld(pinScs, worldCoords);
 * console.log(`Pin: ${pinGps.lat}, ${pinGps.lon}`);
 * ```
 */
export function scsToWorld(
  pointScs: ScsPoint,
  worldCoords: WorldCoordinatesConfig
): GpsPoint {
  // Build course orientation axes from Origin → Reference direction
  const { xAxis, yAxis } = buildCourseAxes(worldCoords);

  // SCS → ENU
  const [dE, dN, dU] = scsToEnu(pointScs, xAxis, yAxis);

  // ENU → GPS (using Origin as the reference point)
  return enuToGeodetic(worldCoords.Origin, dE, dN, dU);
}

/**
 * Convert geodetic coordinates to ENU offsets
 *
 * Reverse of enuToGeodetic. Converts GPS coordinates to local ENU offsets
 * relative to the origin position.
 *
 * @param origin - GPS origin position (reference point)
 * @param lat - Target latitude in decimal degrees
 * @param lon - Target longitude in decimal degrees
 * @param alt - Target altitude in meters
 * @returns ENU offsets in meters [dE, dN, dU]
 */
function geodeticToEnu(
  origin: GpsOrigin,
  lat: number,
  lon: number,
  alt: number
): EnuPoint {
  const lat0Rad = (origin.Latitude * Math.PI) / 180.0;

  const dLatRad = ((lat - origin.Latitude) * Math.PI) / 180.0;
  const dLonRad = ((lon - origin.Longitude) * Math.PI) / 180.0;

  const dN = dLatRad * EARTH_RADIUS;
  const dE = dLonRad * EARTH_RADIUS * Math.cos(lat0Rad);
  const dU = alt - origin.Altitude;

  return [dE, dN, dU];
}

/**
 * Convert ENU offsets to SCS coordinates
 *
 * Inverse transformation of scsToEnu.
 *
 * Because (xAxis, yAxis) form an orthonormal basis in the ground plane,
 * the inverse is a rotation:
 * - X_scs = dE * x_e + dN * x_n
 * - Z_scs = -(dE * y_e + dN * y_n)
 * - Y_scs = dU
 *
 * Note: Z_scs is negated to correct the left/right direction mapping.
 *
 * @param dE - East offset in meters
 * @param dN - North offset in meters
 * @param dU - Up offset in meters
 * @param xAxis - Course direction unit vector
 * @param yAxis - Lateral direction unit vector
 * @returns SCS coordinates [X_scs, Y_scs, Z_scs]
 */
function enuToScs(
  dE: number,
  dN: number,
  dU: number,
  xAxis: UnitVector2D,
  yAxis: UnitVector2D
): ScsPoint {
  const [xE, xN] = xAxis;
  const [yE, yN] = yAxis;

  const xScs = dE * xE + dN * xN;
  // Note: Negating Z_scs to correct left/right direction (inverse transformation)
  const zScs = -(dE * yE + dN * yN);
  const yScs = dU;

  return [xScs, yScs, zScs];
}

/**
 * Convert world GPS coordinates to SCS coordinates
 *
 * Full reverse transformation: GPS → ENU → SCS
 *
 * @param lat - Latitude in decimal degrees
 * @param lon - Longitude in decimal degrees
 * @param alt - Altitude in meters
 * @param worldCoords - World coordinates config (Origin for GPS origin, Reference for direction)
 * @returns SCS coordinates [X_scs, Y_scs, Z_scs]
 */
export function worldToScs(
  lat: number,
  lon: number,
  alt: number,
  worldCoords: WorldCoordinatesConfig
): ScsPoint {
  // Build course axes from Origin → Reference direction
  const { xAxis, yAxis } = buildCourseAxes(worldCoords);

  // GPS → ENU (using Origin as the reference point)
  const [dE, dN, dU] = geodeticToEnu(worldCoords.Origin, lat, lon, alt);

  // ENU → SCS
  return enuToScs(dE, dN, dU, xAxis, yAxis);
}
